import React, { useState, useEffect } from "react";
import { Formik, Form, Field } from "formik";
import { Link, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { getDdOptionForUI } from "../../store/reducer/ddOption/ddOptionSlice";
import { getCountryForUi } from "../../store/reducer/country/countrySlice";
import { addCustomerSite } from "../../store/reducer/customers/customerSiteSlice";
import { getCustomerById } from "../../store/reducer/customers/customersSlice";
import { getCustomerContactForUI } from "../../store/reducer/customers/customerContactSlice";

function AddSite() {
  const { id } = useParams();
  const dispatch = useDispatch();

  const { options = [] } = useSelector((state) => state.ddOptions);
  const { countries = [] } = useSelector((state) => state.countries);

  const [customerData, setCustomerData] = useState([]);
  const [customerContactData, setcustomerContactData] = useState([]);
  const [loading, setLoading] = useState(true);

  /* ──────────────────────────────────────────────────────────
     FETCH STATIC DATA
  ────────────────────────────────────────────────────────── */
  useEffect(() => {
    dispatch(getDdOptionForUI());
    dispatch(getCountryForUi());
    dispatch(getCustomerById(id)).then((result) => {
      if (result.payload) {
        setCustomerData(result.payload);
        setLoading(false);
      }
    });
    dispatch(getCustomerContactForUI(id)).then((result) => {
      if (result.payload) {
        setcustomerContactData(result.payload);
      }
    });
  }, [dispatch, id]);

  /* ──────────────────────────────────────────────────────────
     VALIDATION
  ────────────────────────────────────────────────────────── */
  const validationSchema = Yup.object().shape({
    /* column 1 */
    site_name: Yup.string().required("required"),
    street: Yup.string().required("required"),
    city: Yup.string().required("required"),
    country: Yup.number().required("required"),
    post_code: Yup.string().required("required"),
    customer_contact_id: Yup.number().required("required"),

    /* column 2 */
    billing_name: Yup.string().required("required"),
    billing_street: Yup.string().required("required"),
    billing_city: Yup.string().required("required"),
    billing_post_code: Yup.string().required("required"),
    billing_country: Yup.number().required("required"),
  });

  /* ──────────────────────────────────────────────────────────
     SUBMIT
  ────────────────────────────────────────────────────────── */
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    values.customer_id = customerData.id;
    values.customer_uniqueId = customerData.uniqueId;
    values.createdBy = JSON.parse(localStorage.getItem("userSession")).id;

    const formData = new FormData();
    Object.keys(values).forEach((fieldName) => {
      formData.append(fieldName, values[fieldName]);
    });

    await dispatch(addCustomerSite(formData)).then((result) => {
      if (result.payload) {
        resetForm();
        toast.success(result.payload.message);
      } else {
        toast.error(result.error.message);
      }
    });
  };

  /* ──────────────────────────────────────────────────────────
     RENDER
  ────────────────────────────────────────────────────────── */
  if (loading) return null;

  return (
    <>
      {/* ---------- Heading ---------- */}
      <div className="flex justify-between mb-[30px]">
        <div className="flex gap-[16px] items-center">
          <Link to={`/customer/${id}/site`}>
            <div className="bg-[#F8F8F8] rounded-[10px] p-[8px]">
              <img
                className="w-[24px]"
                src={process.env.PUBLIC_URL + "/icons/back.png"}
                alt="back icon"
              />
            </div>
          </Link>
          <div className="flex flex-col">
            <span className="font-Inter font-[400] leading-[16px] text-[#9E9E9E] text-[14px]">
              Site / Add
            </span>
            <span className="font-Inter font-[700] leading-[30px] text-[24px]">
              Add Site
            </span>
          </div>
        </div>
      </div>

      {/* ---------- Formik ---------- */}
      <Formik
        initialValues={{
          /* column 1 */
          site_name: "",
          street: "",
          city: "",
          country: "",
          post_code: "",
          customer_contact_id: "",

          /* column 2 */
          billing_name: "",
          billing_street: "",
          billing_city: "",
          billing_post_code: "",
          billing_country: "",
          vat: "",
          eori_number: "",

          /* misc */
          is_billable: 0,
          status: "Active",
          copyCustomerDetails: false,
          copyCustomerToBilling: false,
          copySiteToBilling: false,
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          isSubmitting,
          errors,
          touched,
          setFieldValue,
          values
        }) => (
          <Form>
            {/* ---------- Two-Column Grid ---------- */}
            <div className="grid md:grid-cols-2 gap-[24px]">
              {/* ───────── Left column ───────── */}
              <div className="bg-[#F8F8F8] rounded-[12px] p-6 flex flex-col gap-4">
                {/* Copy Customer Details Checkbox */}
                <div className="mb-4">
                  <label className="inline-flex items-center gap-2">
                    <Field
                      type="checkbox"
                      name="copyCustomerDetails"
                      onChange={(e) => {
                        const checked = e.target.checked;
                        if (checked) {
                          setFieldValue("site_name", customerData?.customer_name || "");
                          setFieldValue("street", customerData?.street || "");
                          setFieldValue("city", customerData?.city || "");
                          setFieldValue("country", customerData?.country || "");
                          setFieldValue("post_code", customerData?.post_code || "");
                        }
                      }}
                    />
                    <span className="font-Inter text-[15px] text-[#1C1C20]">Copy Customer Details</span>
                  </label>
                </div>

                {/* Site Name */}
                <FormField
                  label="Site Name"
                  id="site_name"
                  name="site_name"
                  type="text"
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                />

                {/* Street */}
                <FormField
                  label="Site Street"
                  id="street"
                  name="street"
                  component="textarea"
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                />

                {/* City */}
                <FormField
                  label="Site City"
                  id="city"
                  name="city"
                  type="text"
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                />

                {/* Country */}
                <SelectField
                  label="Site Country"
                  id="country"
                  name="country"
                  options={countries}
                  optionLabel="short_name"
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                />

                {/* Post Code */}
                <FormField
                  label="Site Post Code"
                  id="post_code"
                  name="post_code"
                  type="text"
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                />

                {/* Contact */}
                <SelectField
                  label="Site Contact"
                  id="customer_contact_id"
                  name="customer_contact_id"
                  options={customerContactData}
                  optionLabel={(c) => `${c.firstname} ${c.lastname}`}
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                />
              </div>

              {/* ───────── Right column ───────── */}
              <div className="bg-[#F8F8F8] rounded-[12px] p-6 flex flex-col gap-4">
                <div className="mb-4">
                  <label className="inline-flex items-center gap-2 p-2">
                    <Field
                      type="checkbox"
                      name="copyCustomerToBilling"
                      onChange={(e) => {
                        const checked = e.target.checked;
                        if (checked) {
                          setFieldValue("billing_name", customerData?.customer_name || "");
                          setFieldValue("billing_street", customerData?.street || "");
                          setFieldValue("billing_city", customerData?.city || "");
                          setFieldValue("billing_country", customerData?.country || "");
                          setFieldValue("billing_post_code", customerData?.post_code || "");
                        }
                      }}
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Copy Customer Details to Billing
                    </span>
                  </label>

                  <label className="inline-flex items-center gap-2 p-2">
                    <Field
                      type="checkbox"
                      name="copySiteToBilling"
                      onChange={(e) => {
                        const checked = e.target.checked;
                        if (checked) {
                          setFieldValue("billing_name", values.site_name || "");
                          setFieldValue("billing_street", values.street || "");
                          setFieldValue("billing_city", values.city || "");
                          setFieldValue("billing_country", values.country || "");
                          setFieldValue("billing_post_code", values.post_code || "");
                        }
                      }}
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Copy Site Details to Billing
                    </span>
                  </label>
                </div>
                {/* Billing Name */}
                <FormField
                  label="Billing Name"
                  id="billing_name"
                  name="billing_name"
                  type="text"
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                />

                {/* Billing Street */}
                <FormField
                  label="Billing Street"
                  id="billing_street"
                  name="billing_street"
                  component="textarea"
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                />

                {/* Billing City */}
                <FormField
                  label="Billing City"
                  id="billing_city"
                  name="billing_city"
                  type="text"
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                />

                {/* Billing Country */}
                <SelectField
                  label="Billing Country"
                  id="billing_country"
                  name="billing_country"
                  options={countries}
                  optionLabel="short_name"
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                />


                {/* Billing Post Code */}
                <FormField
                  label="Billing Post Code"
                  id="billing_post_code"
                  name="billing_post_code"
                  type="text"
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                />

                {/* Vat */}
                <SelectField
                  label="Vat"
                  id="vat"
                  name="vat"
                  options={options.filter(
                    (o) => o.ui_name === "Finance" && o.field_name === "Tax_Rate"
                  ).sort((a, b) => a.sort_field - b.sort_field)}
                  optionLabel={(o) => `${o.field_code} ${o.field_value}`}
                  optionValue="id"
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                />

                {/* EORI Number */}
                <FormField
                  label="EORI Number"
                  id="eori_number"
                  name="eori_number"
                  type="text"
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                />
              </div>
            </div>

            {/* ---------- Status toggle + Submit ---------- */}
            <div className="flex gap-[24px] bg-[#F8F8F8] rounded-[12px] mt-5 p-4">
              {/* Status Toggle */}
              <div className="flex-1">
                <label className="font-Inter font-[400] text-[15px] text-[#9E9E9E] leading-[17px]">
                  Status
                </label>
                <div>
                  <label
                    htmlFor="status"
                    className="inline-flex items-center px-5 py-2 cursor-pointer text-gray-800 bg-white rounded-[12px]"
                  >
                    <input
                      id="status"
                      type="checkbox"
                      className="hidden peer"
                      name="status"
                      defaultChecked
                      onChange={(e) => {
                        const v = e.target.checked ? "Active" : "Inactive";
                        setFieldValue("status", v);
                      }}
                    />
                    <span className="px-4 py-1 rounded-lg border-gray-600 border peer-checked:border-0">
                      Inactive
                    </span>
                    <span className="px-4 py-1 rounded-lg border-gray-600 peer-checked:border">
                      Active
                    </span>
                  </label>
                </div>
              </div>

              {/* Submit */}
              <div>
                <button
                  type="submit"
                  className={`py-[12px] px-[48px] text-white rounded-[12px] text-[18px] ${
                    isSubmitting ? "bg-gray-300" : "bg-[#1C1C20]"
                  }`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    "Save"
                  )}
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}

/* ──────────────────────────────────────────────────────────
   REUSABLE FIELD COMPONENTS
────────────────────────────────────────────────────────── */
const FormField = ({
  label,
  id,
  name,
  type = "text",
  component = "input",
  errors,
  touched,
  setFieldValue,
}) => (
  <div>
    <label
      className="font-Inter font-[400] text-[15px] text-[#9E9E9E] leading-[17px]"
      htmlFor={id}
    >
      {label}
    </label>
    <Field
      as={component}
      id={id}
      name={name}
      type={type}
      className="block appearance-none w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 rounded-[12px] leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
      onChange={(e) => setFieldValue(name, e.target.value)}
    />
    {errors[name] && touched[name] && (
      <div className="text-red-700">{errors[name]}</div>
    )}
  </div>
);

const SelectField = ({
  label,
  id,
  name,
  options,
  optionLabel,
  optionValue = "id",
  errors,
  touched,
  setFieldValue,
}) => (
  <div>
    <label
      className="font-Inter font-[400] text-[15px] text-[#9E9E9E] leading-[17px]"
      htmlFor={id}
    >
      {label}
    </label>
    <div className="relative">
      <Field
        as="select"
        id={id}
        name={name}
        className="block appearance-none w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded-[12px] leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
        onChange={(e) => setFieldValue(name, e.target.value)}
      >
        <option value="">Select {label.toLowerCase()}</option>
        {options.map((opt) => (
          <option key={opt.id} value={opt[optionValue]}>
            {typeof optionLabel === "function"
              ? optionLabel(opt)
              : opt[optionLabel]}
          </option>
        ))}
      </Field>
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
        <svg
          className="fill-current h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
        >
          <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
        </svg>
      </div>
    </div>
    {errors[name] && touched[name] && (
      <div className="text-red-700">{errors[name]}</div>
    )}
  </div>
);

export default AddSite;
